import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Trash2, Eye, RefreshCw, Calendar, Database, BarChart3, HardDrive, AlertTriangle, Download, Upload } from 'lucide-react';
import bundleService from '../services/bundleService';

const FileManager = ({ files, onFileSelect, onFileDelete, onRefresh, onClearAll, onBundleImport, uploadDisabled, uploadDisabledReason, onRequireAuth }) => {
  const handleDelete = async (file) => {
    if (window.confirm(`Are you sure you want to delete "${file.filename}"?`)) {
      await onFileDelete(file.file_id);
    }
  };

  const handleClearAll = async () => {
    if (!onClearAll) return;
    if (files.length === 0) return;
    const confirmed = window.confirm('This will delete all local datasets and cannot be undone. Continue?');
    if (confirmed) {
      await onClearAll();
    }
  };

  const handleExportBundle = async (file) => {
    try {
      await bundleService.exportBundle({ fileId: file.file_id });
    } catch (error) {
      console.error('Failed to export bundle:', error);
    }
  };

  const handleImportBundle = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (onBundleImport) {
      onBundleImport(file);
    }
    event.target.value = '';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const dateValue = typeof timestamp === 'string'
      ? new Date(timestamp)
      : new Date(timestamp < 1e12 ? timestamp * 1000 : timestamp);
    if (Number.isNaN(dateValue.getTime())) return 'Unknown';
    return dateValue.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatBytes = (bytes) => {
    if (!bytes || Number.isNaN(bytes)) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let value = bytes;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex += 1;
    }
    return `${value.toFixed(value < 10 && unitIndex > 0 ? 1 : 0)} ${units[unitIndex]}`;
  };

  const totalStorageBytes = files.reduce(
    (sum, file) => sum + (Number(file.file_size_bytes) || 0),
    0
  );

  return (
    <div className="max-w-6xl mx-auto space-y-6">

      {uploadDisabled && uploadDisabledReason && (
        <div className="rounded-2xl border border-[rgba(32,30,29,0.12)] bg-sand-100 p-4 text-sm text-sand-700">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>{uploadDisabledReason}</div>
            {onRequireAuth && (
              <button
                type="button"
                onClick={onRequireAuth}
                className="inline-flex items-center justify-center rounded-lg bg-bill-500 px-4 py-2 text-xs font-semibold text-white hover:bg-bill-600 shadow-brand-sm"
              >
                Create free account
              </button>
            )}
          </div>
        </div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-sand-100 rounded-lg border border-[rgba(32,30,29,0.12)]">
              <Database className="w-5 h-5 text-ink" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-ink">File Manager</h2>
              <p className="text-sand-700 tnum">{files.length} files uploaded</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <label className={`inline-flex items-center gap-2 rounded-full border border-[rgba(32,30,29,0.18)] bg-white px-4 py-2 text-xs font-semibold text-ink hover:bg-sand-100 transition-colors cursor-pointer ${uploadDisabled ? 'pointer-events-none opacity-50' : ''}`}>
              <Upload className="w-4 h-4" />
              Import bundle
              <input
                type="file"
                accept=".duckpilot.json,application/json"
                className="hidden"
                onChange={handleImportBundle}
              />
            </label>
            {files.length > 0 && (
              <button
                type="button"
                onClick={handleClearAll}
                className="inline-flex items-center gap-2 rounded-full border border-red-200 bg-red-50 px-4 py-2 text-xs font-semibold text-red-700 hover:bg-red-100 transition-colors"
              >
                <AlertTriangle className="w-4 h-4" />
                Clear all
              </button>
            )}
            <button
              type="button"
              onClick={onRefresh}
              className="inline-flex items-center gap-2 rounded-full border border-[rgba(32,30,29,0.18)] bg-white px-4 py-2 text-xs font-semibold text-ink hover:bg-sand-100 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      </motion.div>


      {files.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="card text-center py-12"
        >
          <div className="w-16 h-16 bg-sand-100 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-[rgba(32,30,29,0.12)]">
            <FileText className="w-8 h-8 text-sand-600" />
          </div>
          <h3 className="text-xl font-semibold text-ink mb-2">No Files Uploaded</h3>
          <p className="text-sand-700 mb-6">Upload your first CSV file to get started with data analysis</p>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {files.map((file, index) => (
            <motion.div
              key={file.file_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="card group hover:bg-sand-100 transition-colors duration-200"
            >

              <div className="flex items-start gap-3 mb-4">
                <div className="p-2 bg-bill-100 rounded-lg flex-shrink-0 border border-bill-200">
                  <FileText className="w-5 h-5 text-bill-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-ink truncate" title={file.filename}>
                    {file.filename}
                  </h3>
                  <p className="text-sm text-sand-700 tnum">
                    {file.row_count?.toLocaleString()} rows • {file.columns?.length} columns
                  </p>
                </div>
              </div>


              <div className="space-y-3 mb-4">
                <div className="flex items-center gap-2 text-sm text-sand-700 tnum">
                  <Calendar className="w-4 h-4" />
                  <span>Uploaded {formatDate(file.upload_time)}</span>
                </div>


                {file.columns && file.columns.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-sand-700 mb-2">Columns:</p>
                    <div className="flex flex-wrap gap-1">
                      {file.columns.slice(0, 3).map((column) => (
                        <span
                          key={column}
                          className="px-2 py-1 bg-white text-sand-700 text-xs rounded border border-[rgba(32,30,29,0.12)]"
                        >
                          {column}
                        </span>
                      ))}
                      {file.columns.length > 3 && (
                        <span className="px-2 py-1 bg-sand-100 text-sand-600 text-xs rounded border border-[rgba(32,30,29,0.12)]">
                          +{file.columns.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>


              <div className="flex items-center gap-2 pt-4 border-t border-[rgba(32,30,29,0.12)]">
                <button
                  type="button"
                  onClick={() => onFileSelect(file)}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-bill-200 bg-bill-100 px-3 py-2 text-xs font-semibold text-bill-700 hover:bg-bill-200 transition-colors"
                >
                  <Eye className="w-4 h-4" />
                  View
                </button>

                <button
                  type="button"
                  onClick={() => handleExportBundle(file)}
                  className="inline-flex items-center justify-center rounded-full border border-[rgba(32,30,29,0.18)] bg-white p-2 text-sand-700 hover:bg-sand-100 transition-colors"
                  title="Export bundle"
                >
                  <Download className="w-4 h-4" />
                </button>

                <button
                  type="button"
                  onClick={() => handleDelete(file)}
                  className="inline-flex items-center justify-center rounded-full border border-red-200 bg-red-50 p-2 text-red-600 hover:bg-red-100 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}


      {files.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          <div className="card text-center">
            <FileText className="w-8 h-8 text-bill-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-ink tnum">{files.length}</div>
            <div className="text-sm text-sand-600">Total Files</div>
          </div>

          <div className="card text-center">
            <Database className="w-8 h-8 text-sage-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-ink tnum">
              {files.reduce((sum, file) => sum + (Number(file.row_count) || 0), 0).toLocaleString()}
            </div>
            <div className="text-sm text-sand-600">Total Rows</div>
          </div>

          <div className="card text-center">
            <BarChart3 className="w-8 h-8 text-bill-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-ink tnum">
              {files.reduce((sum, file) => sum + (file.columns?.length || 0), 0)}
            </div>
            <div className="text-sm text-sand-600">Total Columns</div>
          </div>

          <div className="card text-center">
            <HardDrive className="w-8 h-8 text-bill-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-ink tnum">
              {formatBytes(totalStorageBytes)}
            </div>
            <div className="text-sm text-sand-600">Storage Used</div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default FileManager;
